import {
  Box,
  Drawer,
  Typography,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import { XIcon } from '@phosphor-icons/react';
import { getWindmillJobDetailsApi } from '../../utils/apis';
import { useQuery } from '@tanstack/react-query';
import NoDataFallback from '../common/NoDataFallback';

interface JobDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  nodeData: any;
}

const JobDetailDrawer: React.FC<JobDetailDrawerProps> = ({
  open,
  onClose,
  nodeData,
}) => {
  // Check if this is a main job node (Input or Result)
  const isMainJobNode = nodeData?.isMainJobNode;
  const mainJobData = nodeData?.mainJobData;
  const isInputNode = nodeData?.label === 'Input';
  // const isResultNode = nodeData?.label === 'Result';

  const { data: jobDetailData, isLoading: isJobDetailsLoading } = useQuery({
    queryKey: ['windmill-job-details', nodeData?.job],
    queryFn: () => getWindmillJobDetailsApi(nodeData?.job || ''),
    enabled: !!nodeData?.job && open && !isMainJobNode, // Don't fetch if it's a main job node
  });

  const job = isMainJobNode ? mainJobData : jobDetailData?.data;

  // Helper function to render args as table rows
  const renderArgsTable = (args: any) => {
    if (!args || typeof args !== 'object') {
      return null;
    }

    const entries = Object.entries(args);

    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          mb: 2,
          border: '1px solid #e0e0e0',
          borderRadius: 1,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  backgroundColor: '#f5f5f5',
                  fontSize: '0.875rem',
                  color: 'text.primary',
                }}
              >
                Arg
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  backgroundColor: '#f5f5f5',
                  fontSize: '0.875rem',
                  color: 'text.primary',
                }}
              >
                Value
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(([key, value]) => (
              <TableRow key={key}>
                <TableCell
                  sx={{
                    fontFamily: 'monospace',
                    verticalAlign: 'top',
                    fontSize: '0.875rem',
                  }}
                >
                  {key}
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    verticalAlign: 'top',
                    fontSize: '0.875rem',
                  }}
                >
                  {typeof value === 'object' && value !== null
                    ? JSON.stringify(value, null, 2)
                    : String(value)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Get the appropriate title based on node type
  const getTitle = () => {
    if (nodeData?.label === 'Input') {
      return `Input - Job ${nodeData?.job}`;
    } else if (nodeData?.label === 'Result') {
      return `Result - Job ${nodeData?.job}`;
    } else {
      return `Job Detail ${nodeData?.job}`;
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      slotProps={{
        paper: {
          sx: {
            width: '700px',
            p: 0,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#FFFFFF',
          },
        },
      }}
    >
      {/* Sticky Header Section */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#FFFFFF',
          px: 2,
          pt: 2,
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {/* Close Button and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                '&:hover': { backgroundColor: '#E8EBF1' },
                transition: 'all 0.2s ease',
              }}
            >
              <XIcon size={18} />
            </IconButton>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 18,
                color: 'custom.textBlue',
                letterSpacing: '-0.2px',
              }}
            >
              {getTitle()}
            </Typography>
          </Box>
        </Box>

        <Divider
          sx={{
            borderColor: 'rgba(0, 0, 0, 0.08)',
            mb: 2,
            mx: -2,
          }}
        />
      </Box>

      {/* Scrollable Content Area */}
      <Box
        sx={{
          overflow: 'auto',
          flexGrow: 1,
          px: 2,
          pb: 2,
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {isJobDetailsLoading && !isMainJobNode ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        ) : job ? (
          <>
            <Typography
              variant="body1"
              sx={{
                mb: 1,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <strong>Status:</strong>{' '}
              <Box
                component="span"
                sx={{
                  color: job?.success ? '#4caf50' : '#f44336',
                  fontWeight: 600,
                }}
              >
                {job?.success ? 'Success' : 'Failed'}
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <strong>Duration:</strong>{' '}
              {job?.duration_ms
                ? `${(job.duration_ms / 1000).toFixed(2)}s`
                : 'N/A'}
            </Typography>

            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: 'text.primary',
                mt: 3,
                mb: 2,
              }}
            >
              Arguments
            </Typography>
            {job?.args ? (
              renderArgsTable(job.args)
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: 14,
                  fontStyle: 'italic',
                  mb: 3,
                }}
              >
                No arguments available
              </Typography>
            )}

            {/* Only show Result and Logs sections for Result node, not for Input node */}
            {!isInputNode && (
              <>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: 16,
                    color: 'text.primary',
                    mt: 3,
                    mb: 2,
                  }}
                >
                  Result
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    background: '#f5f5f5',
                    p: 2,
                    borderRadius: 1,
                    mb: 3,
                    maxHeight: 300,
                    overflow: 'auto',
                    border: '1px solid #e0e0e0',
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {job?.result
                    ? JSON.stringify(job.result, null, 2)
                    : 'No result available'}
                </Box>

                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: 16,
                    color: 'text.primary',
                    mb: 2,
                  }}
                >
                  Logs
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    background: '#f5f5f5',
                    p: 2,
                    borderRadius: 1,
                    maxHeight: 250,
                    overflow: 'auto',
                    border: '1px solid #e0e0e0',
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {job?.logs || 'No logs available'}
                </Box>
              </>
            )}
          </>
        ) : (
          <NoDataFallback
            message="No job details found."
            description="There are no job details to display at the moment."
          />
        )}
      </Box>
    </Drawer>
  );
};

export default JobDetailDrawer;
